import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import config from '../config';
import '../styles/changePassword.css';
import Notification from '../components/Notification';
import LoadingSpinner from '../components/LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

const ChangePassword = ({ onClose, onLoginClick }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const validatePassword = (password) => {
        const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;
        return passwordRegex.test(password);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'newPassword') {
            if (!validatePassword(value)) {
                setNewPasswordError('Min 6 caractères avec lettres et chiffres');
            } else {
                setNewPasswordError('');
            }
            setNewPassword(value);
        }

        if (name === 'confirmNewPassword') {
            if (value && newPassword !== value) {
                setConfirmNewPasswordError('Les mots de passe ne correspondent pas.');
            } else {
                setConfirmNewPasswordError('');
            }
            setConfirmNewPassword(value);
        }

        if (name === 'currentPassword') {
            setCurrentPassword(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setNotification({ message: '', type: '', header: '' });
        setIsSubmitting(true);

        if (newPassword !== confirmNewPassword) {
            setNotification({ message: 'Les nouveaux mots de passe ne correspondent pas.', type: 'error', header: 'Erreur' });
            setIsSubmitting(false);
            return;
        }

        if (!validatePassword(newPassword)) {
            setNotification({ message: 'Min 6 caractères avec lettres et chiffres', type: 'error', header: 'Erreur' });
            setIsSubmitting(false);
            return;
        }

        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/users/change-password/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
            });

            if (response.ok) {
                setNotification({ message: 'Mot de passe changé avec succès. Vous allez être déconnecté.', type: 'success', header: 'Succès' });
                setTimeout(() => {
                    // Remove token from both storage locations for consistency
                    Cookies.remove('authToken');
                    sessionStorage.removeItem('authToken');
                    onClose();
                    if (onLoginClick) {
                        onLoginClick();
                    } else {
                        navigate('/dashboard');
                    }
                }, 2000);
            } else {
                const data = await response.json();
                let errorMessage = data.error || 'Une erreur est survenue. Veuillez réessayer.';
                let errorType = 'error';
                
                // Handle rate limiting specifically
                if (response.status === 429) {
                    errorType = 'warning';
                    errorMessage = data.error || 'Trop de tentatives. Veuillez patienter avant de réessayer.';
                }
                
                setNotification({ 
                    message: errorMessage, 
                    type: errorType, 
                    header: response.status === 429 ? 'Limite atteinte' : 'Erreur' 
                });
            }
        } catch (error) {
            setNotification({ message: 'Une erreur est survenue. Veuillez réessayer.', type: 'error', header: 'Erreur' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '', header: '' });
    };

    const isFormValid = () => {
        return (
            currentPassword &&
            newPassword &&
            confirmNewPassword &&
            !newPasswordError &&
            !confirmNewPasswordError
        );
    };

    return (
        <div className="modal">
            <div className="modal-content">
                <span className="close" onClick={onClose}>&times;</span>
                <div className="change-password-container">
                    <Notification
                        message={notification.message}
                        type={notification.type}
                        header={notification.header}
                        onClose={handleNotificationClose}
                    />
                    {isSubmitting && <LoadingSpinner />}
                    <h2 className="change-password-title">Changer le mot de passe</h2>
                    <form className="change-password-form" onSubmit={handleSubmit}>
                        <div className="input-group">
                            <label>Mot de passe actuel</label>
                            <div className={`input-container ${currentPassword ? 'input-container-valid' : ''}`}>
                                <FontAwesomeIcon icon={faLock} className={`icon ${currentPassword ? 'icon-valid' : ''}`} />
                                <input
                                    type="password"
                                    name="currentPassword"
                                    value={currentPassword}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label>Nouveau mot de passe</label>
                            <div className={`input-container ${newPasswordError ? 'input-container-error' : ''} ${!newPasswordError && newPassword ? 'input-container-valid' : ''}`}>
                                <FontAwesomeIcon icon={faLock} className={`icon ${newPasswordError ? 'icon-error' : ''} ${!newPasswordError && newPassword ? 'icon-valid' : ''}`} />
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={handleChange}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '18px'
                                    }}
                                >
                                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                            </div>
                            {newPasswordError ? (
                                <p className="error-message">{newPasswordError}</p>
                            ) : (
                                <p className="password-rule">Min 6 caractères avec lettres et chiffres</p>
                            )}
                        </div>
                        <div className="input-group">
                            <label>Confirmer le nouveau mot de passe</label>
                            <div className={`input-container ${confirmNewPasswordError ? 'input-container-error' : ''} ${!confirmNewPasswordError && confirmNewPassword ? 'input-container-valid' : ''}`}>
                                <FontAwesomeIcon icon={faLock} className={`icon ${confirmNewPasswordError ? 'icon-error' : ''} ${!confirmNewPasswordError && confirmNewPassword ? 'icon-valid' : ''}`} />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmNewPassword"
                                    value={confirmNewPassword}
                                    onChange={handleChange}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '18px'
                                    }}
                                >
                                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                            </div>
                            {confirmNewPasswordError && <p className="error-message">{confirmNewPasswordError}</p>}
                        </div>

                        <button
                            type="submit"
                            className="change-password-button"
                            disabled={isSubmitting || !isFormValid()}
                        >
                            {isSubmitting ? <LoadingSpinner /> : 'Changer le mot de passe'}
                        </button>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;